import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Package,
  Grid,
  LayoutDashboard,
  ArrowRight,
  Printer,
} from "lucide-react";
import { formatNumber, getUploadUrl } from "@/lib/utils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PrepressWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as any;
  const orderDetailIds: string[] = search.orderDetailIds || [];

  const [customUps, setCustomUps] = useState<Record<string, number>>({});

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["prepress-preview", orderDetailIds],
    queryFn: async () => {
      if (!orderDetailIds.length) return [];
      return await trpcClient.productionOrders.generatePrepressLayout.query({
        orderDetailIds,
      });
    },
    enabled: orderDetailIds.length > 0,
  });

  const createLayoutsMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      return await trpcClient.productionOrders.createLayoutsFromOrderDetails.mutate({
        orderDetailIds: ids,
      });
    },
    onSuccess: (data) => {
      toast.success(`Đã tạo thành công ${data.length} Bản Bình (Layout)`);
      queryClient.invalidateQueries({ queryKey: ["print-layouts"] });
      navigate({ to: "/production/print-layouts" });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi tạo bản bình");
    },
  });

  const handleCreateLayouts = () => {
    if (!orderDetailIds.length) return;
    createLayoutsMutation.mutate(orderDetailIds);
  };

  const handleUpsChange = (detailId: string, val: string) => {
    const num = parseInt(val, 10);
    setCustomUps((prev) => ({
      ...prev,
      [detailId]: isNaN(num) || num < 1 ? 1 : num,
    }));
  };

  if (!orderDetailIds.length) {
    return (
      <div className="p-8 text-center text-slate-500">
        Vui lòng chọn các Order Detail cần ghép bài từ màn hình trước.
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 py-4 space-y-4">
      <div className="flex justify-between items-center bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-xs">
        <div className="text-sm font-semibold text-slate-800">Tạo Bản Bình Nhanh (Prepress Wizard)</div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate({ to: "/production-orders" })}
          >
            Hủy
          </Button>
          <Button
            onClick={handleCreateLayouts}
            disabled={createLayoutsMutation.isPending || isLoading}
          >
            <Printer className="w-4 h-4 mr-2" />
            Lưu Layout Bình Bài
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">
            Đang phân tích Layout...
          </div>
        ) : (
          groups.map((group: any, idx: number) => {
            // Group Calculation
            const items = group.items;
            const maxSheets = items.reduce((max: number, item: any) => {
              const ups = customUps[item.orderDetailId] || 1;
              const sheets = Math.ceil(item.plannedQty / ups);
              return sheets > max ? sheets : max;
            }, 0);

            return (
              <Card key={idx} className="border-slate-200 shadow-sm">
                <CardHeader className="bg-slate-50 border-b border-slate-100 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <LayoutDashboard className="w-5 h-5 text-indigo-600" />
                      <CardTitle className="text-base font-semibold text-slate-800">
                        Layout Nhóm {idx + 1}
                      </CardTitle>
                      <Badge variant="outline" className="bg-white">
                        {group.layoutId}
                      </Badge>
                    </div>
                    <div className="text-sm font-medium text-slate-600">
                      Dự kiến Số tờ in (chưa bù hao):{" "}
                      <span className="text-indigo-600 font-bold">
                        {formatNumber(maxSheets)}
                      </span>{" "}
                      tờ
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {items.map((item: any) => {
                      const ups = customUps[item.orderDetailId] || 1;
                      const requiredSheets = Math.ceil(item.plannedQty / ups);
                      return (
                        <div
                          key={item.orderDetailId}
                          className="flex items-start gap-4 p-4 border border-slate-100 rounded-lg bg-white"
                        >
                          <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                            {item.product?.image ? (
                              <img
                                src={getUploadUrl(item.product.image)}
                                className="w-10 h-10 object-contain"
                                alt=""
                              />
                            ) : (
                              <Package className="w-6 h-6 text-slate-300" />
                            )}
                          </div>

                          <div className="flex-1 grid grid-cols-4 gap-4">
                            <div className="col-span-2">
                              <h4 className="font-semibold text-slate-800 text-sm">
                                {item.productName}
                              </h4>
                              <div className="text-xs text-slate-500 font-mono mt-1">
                                SO: {item.salesOrderId}
                              </div>
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">
                                  Order Qty:
                                </span>
                                <span className="font-medium">
                                  {formatNumber(item.orderQty)}
                                </span>
                              </div>
                              <div className="flex justify-between text-green-600">
                                <span>Tồn kho (FG):</span>
                                <span>- {formatNumber(item.inventoryQty)}</span>
                              </div>
                              <div className="flex justify-between border-t border-slate-100 pt-1 font-semibold text-indigo-600">
                                <span>Sản xuất (Planned):</span>
                                <span>{formatNumber(item.plannedQty)}</span>
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs text-slate-500 mb-1 block">
                                Số Ups / Tờ in
                              </Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min={1}
                                  className="h-8 w-20 text-center font-medium"
                                  value={ups}
                                  onChange={(e) =>
                                    handleUpsChange(
                                      item.orderDetailId,
                                      e.target.value,
                                    )
                                  }
                                />
                                <span className="text-xs text-slate-400">
                                  ups
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 mt-2">
                                Yêu cầu:{" "}
                                <strong>{formatNumber(requiredSheets)}</strong>{" "}
                                tờ
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
